import React, { useState } from "react"
import toast, { Toaster } from "react-hot-toast"

import { trpc } from "../../lib/trpc"

interface NewsSourceFormData {
  name: string
  url: string
  feedType: "rss" | "scrape"
  cssSelectors?: {
    title?: string
    content?: string
    date?: string
    url?: string
  }
}

interface XSourceFormData {
  name: string
  username: string
  includeReplies: boolean
  includeRetweets: boolean
  maxTweetsPerFetch: number
}

const SourceManager: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"list" | "add-news" | "add-x">("list")
  const [newsFormData, setNewsFormData] = useState<NewsSourceFormData>({
    name: "",
    url: "",
    feedType: "rss",
  })
  const [xFormData, setXFormData] = useState<XSourceFormData>({
    name: "",
    username: "",
    includeReplies: false,
    includeRetweets: false,
    maxTweetsPerFetch: 50,
  })

  // Queries
  const { data: sources, refetch: refetchSources } = trpc.contentSources.listSources.useQuery({
    limit: 50,
  })

  const { data: stats } = trpc.contentSources.getSourceStats.useQuery()

  // Mutations
  const createNewsSource = trpc.contentSources.createNewsSource.useMutation({
    onSuccess: () => {
      toast.success("News source created successfully!")
      void refetchSources()
      setActiveTab("list")
      setNewsFormData({ name: "", url: "", feedType: "rss" })
    },
    onError: (error) => {
      const errorMessage = error.message || "Unknown error occurred"
      toast.error(`Failed to create news source: ${errorMessage}`)
      console.error("News source creation error:", error)
    },
  })

  const createXSource = trpc.contentSources.createXSource.useMutation({
    onSuccess: () => {
      toast.success("X/Twitter source created successfully!")
      void refetchSources()
      setActiveTab("list")
      setXFormData({
        name: "",
        username: "",
        includeReplies: false,
        includeRetweets: false,
        maxTweetsPerFetch: 50,
      })
    },
    onError: (error) => {
      const errorMessage = error.message || "Unknown error occurred"
      toast.error(`Failed to create X/Twitter source: ${errorMessage}`)
      console.error("X source creation error:", error)
    },
  })

  const toggleSource = trpc.contentSources.toggleSource.useMutation({
    onSuccess: () => {
      toast.success("Source status updated successfully!")
      void refetchSources()
    },
    onError: (error) => {
      const errorMessage = error.message || "Unknown error occurred"
      toast.error(`Failed to update source: ${errorMessage}`)
      console.error("Toggle source error:", error)
    },
  })

  const triggerIngestion = trpc.contentIngestion.ingestContent.useMutation({
    onSuccess: () => {
      toast.success("Content ingestion started successfully!")
    },
    onError: (error) => {
      const errorMessage = error.message || "Unknown error occurred"
      toast.error(`Failed to trigger ingestion: ${errorMessage}`)
      console.error("Ingestion trigger error:", error)
    },
  })

  const handleNewsSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    createNewsSource.mutate({
      name: newsFormData.name,
      url: newsFormData.url,
      feedType: newsFormData.feedType,
      cssSelectors: newsFormData.cssSelectors,
    })
  }

  const handleXSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    createXSource.mutate({
      name: xFormData.name,
      username: xFormData.username,
      includeReplies: xFormData.includeReplies,
      includeRetweets: xFormData.includeRetweets,
      maxTweetsPerFetch: xFormData.maxTweetsPerFetch,
    })
  }

  const handleToggleSource = (sourceId: string, isActive: boolean) => {
    toggleSource.mutate({ id: sourceId, isActive })
  }

  const handleTriggerIngestion = (sourceId: string) => {
    triggerIngestion.mutate({ sourceId })
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-2">Content Sources</h1>
        <p className="">Manage news sources and X accounts for content ingestion</p>

        {stats && (
          <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-blue-50 p-3 rounded-lg">
              <div className="text-sm ">Total Sources</div>
              <div className="text-xl font-semibold">{stats.totalSources}</div>
            </div>
            <div className="bg-green-50 p-3 rounded-lg">
              <div className="text-sm ">Active</div>
              <div className="text-xl font-semibold">{stats.activeSources}</div>
            </div>
            <div className="bg-orange-50 p-3 rounded-lg">
              <div className="text-sm ">News</div>
              <div className="text-xl font-semibold">{stats.newsSources}</div>
            </div>
            <div className="bg-purple-50 p-3 rounded-lg">
              <div className="text-sm ">X/Twitter</div>
              <div className="text-xl font-semibold">{stats.xSources}</div>
            </div>
            <div className="bg-cyan-50 p-3 rounded-lg">
              <div className="text-sm ">Recently Ingested</div>
              <div className="text-xl font-semibold">{stats.recentlyIngested}</div>
            </div>
          </div>
        )}
      </div>

      <div className="mb-6">
        <div className="flex space-x-4 border-b">
          <button
            onClick={() => setActiveTab("list")}
            className={`pb-2 px-4 ${
              activeTab === "list" ? "border-b-2 border-blue-500 " : "text-gray-500 hover:text-primary"
            }`}
          >
            Sources List
          </button>
          <button
            onClick={() => setActiveTab("add-news")}
            className={`pb-2 px-4 ${
              activeTab === "add-news" ? "border-b-2 border-blue-500 " : "text-gray-500 hover:text-primary"
            }`}
          >
            Add News Source
          </button>
          <button
            onClick={() => setActiveTab("add-x")}
            className={`pb-2 px-4 ${
              activeTab === "add-x" ? "border-b-2 border-blue-500 " : "text-gray-500 hover:text-primary"
            }`}
          >
            Add X Account
          </button>
        </div>
      </div>

      {activeTab === "list" && (
        <div className="space-y-4">
          {sources?.sources.map((source) => (
            <div key={source.id} className="border rounded-lg p-4 flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <h3 className="font-semibold">{source.name}</h3>
                  <span
                    className={`px-2 py-1 text-xs rounded-full ${
                      source.type === "news" ? "bg-orange-100 text-orange-800" : "bg-purple-100 text-purple-800"
                    }`}
                  >
                    {source.type === "news" ? "News" : "X/Twitter"}
                  </span>
                  <span
                    className={`px-2 py-1 text-xs rounded-full ${
                      source.isActive ? "bg-green-100 text-green-800" : "bg-foregroundx text-gray-800"
                    }`}
                  >
                    {source.isActive ? "Active" : "Inactive"}
                  </span>
                </div>
                <div className="text-sm  mt-1">
                  {source.type === "news" ? (source.config as any)?.url : `@${(source.config as any)?.username}`}
                </div>
                {source.lastIngestedAt && (
                  <div className="text-xs text-gray-500 mt-1">
                    Last ingested: {new Date(source.lastIngestedAt).toLocaleString()}
                  </div>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleTriggerIngestion(source.id)}
                  disabled={!source.isActive || triggerIngestion.isLoading}
                  className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 disabled:opacity-50"
                >
                  {triggerIngestion.isLoading ? "Running..." : "Ingest Now"}
                </button>
                <button
                  onClick={() => handleToggleSource(source.id, !source.isActive)}
                  disabled={toggleSource.isLoading}
                  className={`px-3 py-1 text-sm rounded ${
                    source.isActive
                      ? "bg-red-500 text-white hover:bg-red-600"
                      : "bg-green-500 text-white hover:bg-green-600"
                  } disabled:opacity-50`}
                >
                  {source.isActive ? "Disable" : "Enable"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === "add-news" && (
        <form onSubmit={handleNewsSubmit} className="space-y-4 max-w-lg">
          <div>
            <label className="block text-sm font-medium mb-1">Source Name</label>
            <input
              type="text"
              value={newsFormData.name}
              onChange={(e) => setNewsFormData({ ...newsFormData, name: e.target.value })}
              className="w-full border rounded-lg px-3 py-2"
              placeholder="e.g., TechCrunch AI News"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Feed URL</label>
            <input
              type="url"
              value={newsFormData.url}
              onChange={(e) => setNewsFormData({ ...newsFormData, url: e.target.value })}
              className="w-full border rounded-lg px-3 py-2"
              placeholder="https://example.com/rss"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Feed Type</label>
            <select
              value={newsFormData.feedType}
              onChange={(e) => setNewsFormData({ ...newsFormData, feedType: e.target.value as "rss" | "scrape" })}
              className="w-full border rounded-lg px-3 py-2"
            >
              <option value="rss">RSS Feed</option>
              <option value="scrape">Web Scraping</option>
            </select>
          </div>

          {newsFormData.feedType === "scrape" && (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Title CSS Selector</label>
                <input
                  type="text"
                  value={newsFormData.cssSelectors?.title || ""}
                  onChange={(e) =>
                    setNewsFormData({
                      ...newsFormData,
                      cssSelectors: { ...newsFormData.cssSelectors, title: e.target.value },
                    })
                  }
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder=".article-title, h1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Content CSS Selector</label>
                <input
                  type="text"
                  value={newsFormData.cssSelectors?.content || ""}
                  onChange={(e) =>
                    setNewsFormData({
                      ...newsFormData,
                      cssSelectors: { ...newsFormData.cssSelectors, content: e.target.value },
                    })
                  }
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder=".article-content, .post-body"
                />
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={createNewsSource.isLoading}
            className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 disabled:opacity-50"
          >
            {createNewsSource.isLoading ? "Creating..." : "Create News Source"}
          </button>
        </form>
      )}

      {activeTab === "add-x" && (
        <form onSubmit={handleXSubmit} className="space-y-4 max-w-lg">
          <div>
            <label className="block text-sm font-medium mb-1">Source Name</label>
            <input
              type="text"
              value={xFormData.name}
              onChange={(e) => setXFormData({ ...xFormData, name: e.target.value })}
              className="w-full border rounded-lg px-3 py-2"
              placeholder="e.g., Elon Musk Updates"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">X Username</label>
            <input
              type="text"
              value={xFormData.username}
              onChange={(e) => setXFormData({ ...xFormData, username: e.target.value })}
              className="w-full border rounded-lg px-3 py-2"
              placeholder="elonmusk (without @)"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Max Tweets Per Fetch</label>
            <input
              type="number"
              value={xFormData.maxTweetsPerFetch}
              onChange={(e) => setXFormData({ ...xFormData, maxTweetsPerFetch: parseInt(e.target.value) })}
              className="w-full border rounded-lg px-3 py-2"
              min="1"
              max="100"
            />
          </div>

          <div className="space-y-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={xFormData.includeReplies}
                onChange={(e) => setXFormData({ ...xFormData, includeReplies: e.target.checked })}
                className="mr-2"
              />
              Include replies
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={xFormData.includeRetweets}
                onChange={(e) => setXFormData({ ...xFormData, includeRetweets: e.target.checked })}
                className="mr-2"
              />
              Include retweets
            </label>
          </div>

          <button
            type="submit"
            disabled={createXSource.isLoading}
            className="bg-purple-500 text-white px-4 py-2 rounded-lg hover:bg-purple-600 disabled:opacity-50"
          >
            {createXSource.isLoading ? "Creating..." : "Create X Source"}
          </button>
        </form>
      )}

      {/* Toast Notifications */}
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 4000,
          style: {
            background: "#333",
            color: "#fff",
          },
          success: {
            style: {
              background: "#10B981",
            },
          },
          error: {
            style: {
              background: "#EF4444",
            },
          },
        }}
      />
    </div>
  )
}

export default SourceManager
